<script setup lang="ts">
import { computed, ref } from 'vue'
import { feedbackDialog, feedbackListUpdate } from '../service/FeedbackDialog/index'
import { PAGE_SIZE_OPTIONS, DEFAULT_PAGE_SIZE } from '@/constants'
// 复用提交页面的axios（全局已配拦截器）
import axios from 'axios'
import config from '@/utils/config'
import { ElMessage } from 'element-plus'

// 定义 emit
const emit = defineEmits(['close'])

// 完全复用提交页面的BaseUrl
const BaseUrl = config.BASE_URL

// 原有列表项类型（字段名和后端返回对齐）
interface feedbackItem {
  pageName: string
  moduleName: string
  issueDetails: string
  submissionId: number
  tag: string
}

// 详情数据类型（字段名和提交页面的Form对齐）
interface IssueDetail {
  submissionId: number
  submissionTime: string
  submitter: string
  pageName: string
  moduleName: string
  issueDetails: string
  issueImage: string | null
  tag: string
  resolver: string
  resolveTime: string
}

// 页面可视（原有）
const visible = ref(false)
// 详情弹窗可视
const detailVisible = ref(false)

// 列表数据（原有）
const feedbackList = ref<feedbackItem[]>([])
// 详情数据（初始值和提交页面Form一致）
const detailForm = ref<IssueDetail>({
  submissionId: 0,
  submissionTime: '',
  submitter: '',
  pageName: '',
  moduleName: '',
  issueDetails: '',
  issueImage: null,
  tag: '未解决',
  resolver: '',
  resolveTime: ''
})

// 分页（原有）
const currentPage = ref(1)
const pageSize = ref(DEFAULT_PAGE_SIZE)
// 列表头（原有）
const tableHeaderFeedbackInfo = ['页面', '模块', '问题详情', '状态','查看详情']

// 打开列表弹窗（原有）
const open = async () => {
  visible.value = true
  currentPage.value = 1
  const response = await feedbackDialog()
  console.log('列表数据:', response)
  if (response) {
    feedbackList.value = response as feedbackItem[]
  }
}

defineExpose({ open })
const handleClose=()=> {
  visible.value = false
  // 触发 close 事件，通知父组件重置图标
  emit('close')
}
// 关闭详情弹窗
const handleDetailClose = () => {
  detailVisible.value = false
  resetDetailForm()
}

// 重置详情表单
const resetDetailForm = () => {
  detailForm.value = {
    submissionId: 0,
    submissionTime: '',
    submitter: '',
    pageName: '',
    moduleName: '',
    issueDetails: '',
    issueImage: null,
    tag: '',
    resolver: '',
    resolveTime: ''
  }
}

// 计算当前页数据（原有）
const currentData = computed(() => {
  const sorted = feedbackList.value
    .map((item: feedbackItem, idx: number) => ({ item, idx }))
    .sort((a: { item: feedbackItem; idx: number }, b: { item: feedbackItem; idx: number }) => {
      const ra = a.item.tag === '未解决' ? 0 : 1
      const rb = b.item.tag === '未解决' ? 0 : 1
      if (ra !== rb) return ra - rb
      return a.idx - b.idx
    })
    .map((x: { item: feedbackItem; idx: number }) => x.item)
  const startIndex = (currentPage.value - 1) * pageSize.value
  const endIndex = startIndex + pageSize.value
  return sorted.slice(startIndex, endIndex)
})

// 分页事件（原有）
function handlePageChange(page: number) {
  currentPage.value = page
}
function handleSizeChange() {
  currentPage.value = 1
}

// 查看详情核心方法（完全复用提交页面的axios请求风格）
const handleView = async (submissionId: number) => {
  try {
    // 关键：和提交页面用同一个axios，手动加Token（全局拦截器可能漏加GET请求）
    console.log('获取详情 submissionId:', submissionId)

    const response = await axios.get(`${BaseUrl}/api/issueList/${submissionId}`, {})

    console.log('详情数据:', response.data)
    // 适配后端返回格式（和提交页面的响应处理一致）
    if (response.data && response.data.code === 200) {
      const data = response.data.data
      detailForm.value = {
        submissionId: data.submissionId || 0,
        submissionTime: data.submissionTime || '',
        submitter: data.submitter || '',
        pageName: data.pageName || '',
        moduleName: data.moduleName || '',
        issueDetails: data.issueDetails || '',
        issueImage: data.issueImage || null,
        tag: data.tag || '',
        resolver: data.resolver || '',
        resolveTime: data.resolveTime || ''
      }
      detailVisible.value = true
    } else {
      console.error(response.data?.msg || '获取详情失败')
    }
  } catch (error: any) {
    console.error('获取详情失败:', error)
    // 精准处理401（和提交页面的错误处理风格一致）
    if (error.response?.status === 401) {
      console.error('登录已过期，请重新登录')
      localStorage.removeItem('token')
      window.location.href = '/login' // 跳登录页
    } else if (error.response?.status === 404) {
      console.error('该反馈记录不存在')
    } else {
      console.error('获取详情失败，请重试')
    }
  }
}

const submitFeedbackList = async () => {
  const { submissionId, tag, resolver, resolveTime } = detailForm.value

  if (!submissionId) {
    console.error('参数错误')
    return
  }

  if (tag === '解决') {
    if (!resolver) {
      ElMessage.warning('请填写解决人')
      return
    }
    if (!resolveTime) {
      ElMessage.warning('请选择解决时间')
      return
    }
  }

  try {
    const res = await feedbackListUpdate(submissionId, {
      tag,
      resolver,
      resolveTime
    })

    console.log("返回的的解决：" , res)
    if (res === '更新成功') {
      ElMessage.success(res)
      detailVisible.value = false
      await open() // 刷新列表
    }
  } catch (e) {
    console.error('更新失败')
  }
}

const cancelFeedbackList = () => {
  detailVisible.value = false
  resetDetailForm()
}
// 暴露方法（原有）
// defineExpose({ open })
</script>

<template>
  <!-- 原有列表弹窗（完全保留） -->
  <el-dialog
    title="问题反馈列表"
    v-model="visible"
    width="720px"
    @close="handleClose"
    :close-on-click-modal="false"
  >
    <div class="table-container">
      <table class="media-table">
        <thead>
        <tr class="table-header">
          <th v-for="header in tableHeaderFeedbackInfo" :key="header" class="header-cell">
            {{ header }}
          </th>
        </tr>
        </thead>
        <tbody class="table-body">
        <tr class="table-row" v-for="(item, index) in currentData" :key="index">
          <td class="data-cell">{{ item.pageName }}</td>
          <td class="data-cell">{{ item.moduleName }}</td>
          <td class="data-cell">{{ item.issueDetails }}</td>
          <td class="data-cell">
            <el-tag :type="item.tag === '解决' ? 'success' : 'warning'">
              {{ item.tag }}
            </el-tag>
          </td>
          <td class="data-cell">
            <button class="view-btn" @click="handleView(item.submissionId)">查看</button>
          </td>
        </tr>
        </tbody>
      </table>
    </div>
    <div class="pagination-container">
      <el-pagination
        v-model:current-page="currentPage"
        v-model:page-size="pageSize"
        :total="feedbackList.length"
        @current-change="handlePageChange"
        @size-change="handleSizeChange"
        :page-sizes="PAGE_SIZE_OPTIONS"
        layout="total, prev, pager, next, sizes, jumper"
        background
        small
      />
    </div>
  </el-dialog>

  <!-- 详情弹窗（完全复用提交页面的样式/布局） -->
  <el-dialog
    title="问题反馈详情"
    v-model="detailVisible"
    width="720px"
    @close="handleDetailClose"
    class="feedback-dialog"
    :close-on-click-modal="false"
  >
    <el-form :model="detailForm" label-width="100px" class="feedback-form" label-position="left">
      <el-row :gutter="20">
        <el-col :span="12">
          <el-form-item label="页面">
            <el-input v-model="detailForm.pageName" disabled />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="模块">
            <el-input v-model="detailForm.moduleName" disabled />
          </el-form-item>
        </el-col>
      </el-row>

      <el-row :gutter="20">
        <el-col :span="12">
          <el-form-item label="提交人">
            <el-input v-model="detailForm.submitter" disabled />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="提交时间">
            <el-input v-model="detailForm.submissionTime" disabled />
          </el-form-item>
        </el-col>
      </el-row>

      <el-row :gutter="20">
        <el-col :span="12">
          <el-form-item label="状态">
            <el-select
              v-model="detailForm.tag"
              placeholder="请选择状态"
              style="width: 100%"
            >
              <el-option label="未解决" value="未解决" />
              <el-option label="解决" value="解决" />
            </el-select>
          </el-form-item>
        </el-col>
      </el-row>

      <el-row :gutter="20">
        <el-col :span="12">
          <el-form-item label="解决人">
            <el-input v-model="detailForm.resolver" />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="解决时间">
            <el-date-picker
              v-model="detailForm.resolveTime"
              type="datetime"
              placeholder="请选择解决时间"
              format="YYYY-MM-DD HH:mm:ss"
              value-format="YYYY-MM-DD HH:mm:ss"
              style="width: 100%"
            />
          </el-form-item>
        </el-col>
      </el-row>

      <el-row>
        <el-col :span="24">
          <el-form-item label="问题详情">
            <el-input v-model="detailForm.issueDetails" type="textarea" rows="6" disabled />
          </el-form-item>
        </el-col>
      </el-row>

      <el-row>
        <el-col :span="24">
          <el-form-item>
            <template #label> 问题图片 </template>
            <!-- 复用提交页面的图片样式 -->
            <div v-if="detailForm.issueImage" class="upload-preview">
              <el-image
                :src="detailForm.issueImage"
                fit="contain"
                class="preview-image"
                :preview-src-list="[detailForm.issueImage]"
              />
            </div>
            <div v-else class="upload-placeholder">
              <span class="upload-text">暂无图片</span>
            </div>
          </el-form-item>
        </el-col>
      </el-row>

      <el-row class="action-row">
        <el-col :span="24" class="button-col">
          <div class="button-group">
            <el-button type="primary" @click="submitFeedbackList">提交</el-button>
            <el-button @click="cancelFeedbackList">取消</el-button>
          </div>
        </el-col>
      </el-row>
    </el-form>
  </el-dialog>
</template>

<style scoped>
/* 原有列表样式（完全保留） */
.table-container {
  width: 100%;
  overflow-y: auto;
  border-radius: 4px;
  max-width: 100%;
  max-height: 500px;
}
.media-table {
  width: 100%;
  min-width: 100%;
  border-collapse: collapse;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  font-size: 14px;
  table-layout: fixed;
}
.table-header {
  background-color: #f5f5f5;
}
.header-cell {
  padding: 12px 16px;
  text-align: center;
  font-weight: 500;
  color: #333333;
  border-bottom: 1px solid #e0e0e0;
  white-space: nowrap;
  position: sticky;
  top: 0;
  background-color: #f5f5f5;
  z-index: 1;
  overflow: hidden;
  text-overflow: ellipsis;
}
.table-row {
  background-color: #ffffff;
  transition: background-color 0.2s ease;
}
.data-cell {
  padding: 12px 16px;
  color: #666666;
  white-space: nowrap;
  vertical-align: middle;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: center;
}
.view-btn {
  color: #1890ff;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px 8px;
  font-size: 14px;
}
.pagination-container {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 16px 5px;
  margin-top: 16px;
  border-top: 1px solid #f0f0f0;
  background-color: #ffffff;
}

/* 详情弹窗样式（完全复用提交页面） */
.feedback-form {
  padding: 32px;
}
.upload-placeholder {
  border: 2px dashed #dcdfe6;
  border-radius: 8px;
  padding: 40px 20px;
  text-align: center;
  background: #fafafa;
  transition: all 0.3s ease;
  width: 100%;
}
.upload-text {
  color: #909399;
  font-size: 14px;
}
.upload-preview {
  position: relative;
  border: 2px dashed #dcdfe6;
  border-radius: 8px;
  padding: 8px;
  text-align: center;
  background: #fafafa;
  transition: all 0.3s ease;
  width: 100%;
  box-sizing: border-box;
}
.preview-image {
  width: 100%;
  max-height: 240px;
  object-fit: contain;
  border-radius: 6px;
}

.button-col {
  display: flex;
  justify-content: flex-end;
}
.button-group {
  display: flex;
  gap: 12px;
}
</style>
